/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';

import { Modal } from 'flowbite-react';
import { BarWave } from 'react-cssfx-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faSearch, faEdit, faTrash, faLightbulb, faEye, faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';
import {
  useGetLessonsQuery,
  useDeleteLessonMutation,
  useCreateLessonMutation,
  useEditLessonMutation,
} from '../../redux/apiSlice';
import Pagination from '../components/Pagination/Pagination';

const PageSize = 10;

const Lessons = () => {
  const [lessonForm, setLessonForm] = useState({
    title: '',
    description: '',
    source: '',
    sourceDetail: '',
    dateReported: new Date().toISOString().split('T')[0],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredLessons, setFilteredLessons] = useState([]);
  const [currentLessonId, setCurrentLessonId] = useState(null);
  const { data: lessons, error, refetch } = useGetLessonsQuery();

  const [deletionStatus, setDeletionStatus] = useState(false);

  const [deleteLesson] = useDeleteLessonMutation();
  const [createLesson] = useCreateLessonMutation();
  const [editLesson] = useEditLessonMutation();

  useEffect(() => {
    if (lessons) {
      setFilteredLessons(lessons);
    }
  }, [lessons]);

  useEffect(() => {
    if (lessons) {
      refetch();
      const query = searchQuery.toLowerCase() || '';

      setFilteredLessons(
        lessons.filter((lesson) => {
          const title = lesson.title?.toLowerCase() || '';
          const description = lesson.description?.toLowerCase() || '';
          const source = lesson.source?.toLowerCase() || '';
          return title.includes(query)
          || description.includes(query)
          || source.includes(query);
        }),
      );
    }
  }, [searchQuery, lessons, refetch]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Helper function to get button text
  const getButtonText = () => {
    if (isSubmitting) return 'Saving...';
    if (openAddModal) return 'Create Lesson';
    return 'Update Lesson';
  };

  const handleDeleteLesson = async (lessonId) => {
    setCurrentLessonId(lessonId);
    setOpenDeleteModal(true);
  };

  const confirmDeleteLesson = async () => {
    try {
      await deleteLesson(currentLessonId);
      setSuccessMsg('Lesson deleted successfully.');
      setTimeout(() => {
        setSuccessMsg('');
      }, 2000);
      setDeletionStatus(true);
      setOpenDeleteModal(false);
    } catch (error) {
      setErrorMsg('Failed to delete lesson.');
      setTimeout(() => {
        setErrorMsg('');
      }, 2000);
    }
  };

  const handleAddLesson = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await createLesson(lessonForm).unwrap();
      setSuccessMsg('Lesson created successfully!');
      setLessonForm({
        title: '',
        description: '',
        source: '',
        sourceDetail: '',
        date_reported: new Date().toISOString().split('T')[0],
      });
      setOpenAddModal(false);
      refetch();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (error) {
      setErrorMsg(error?.data?.message || 'Failed to create lesson');
      setTimeout(() => setErrorMsg(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditLesson = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await editLesson({
        lessonId: currentLessonId,
        updates: lessonForm,
      }).unwrap();
      setSuccessMsg('Lesson updated successfully!');
      setOpenEditModal(false);
      refetch();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (error) {
      setErrorMsg(error?.data?.message || 'Failed to update lesson');
      setTimeout(() => setErrorMsg(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditModal = (lesson) => {
    setCurrentLessonId(lesson.id);
    setLessonForm({
      title: lesson.title || '',
      description: lesson.description || '',
      source: lesson.source || '',
      sourceDetail: lesson.sourceDetail || '',
      dateReported: lesson.dateReported ? new Date(lesson.dateReported).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    });
    setOpenEditModal(true);
  };

  useEffect(() => {
    if (deletionStatus) {
      refetch();
      setDeletionStatus(false);
    }
  }, [deletionStatus, refetch]);

  if (error) {
    // Error handling for lessons fetching
  }

  const paginatedLessons = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return filteredLessons.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, filteredLessons]);

  if (!lessons) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <BarWave color="#6366f1" height="2rem" width="8rem" />
          <p className="mt-4 text-gray-300">Loading lessons...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full min-h-screen bg-gray-900">
      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1 bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <FontAwesomeIcon icon={faLightbulb} className="text-white text-lg" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-white">Lessons Learned Management</h1>
                  <p className="text-gray-300 text-sm">Manage safety lessons and insights</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpenAddModal(true)}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="text-sm" />
                <span className="font-medium">Create Lesson</span>
              </button>
            </div>
          </div>

          {/* Search Section */}
          <div className="px-6 py-4 bg-gray-750 border-b border-gray-600">
            <div className="flex items-center justify-between">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search lessons..."
                    value={searchQuery}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400"
                  />
                </div>
              </div>
              <div className="ml-4 text-sm text-gray-300">
                {filteredLessons.length}
                {' '}
                lesson
                {filteredLessons.length !== 1 ? 's' : ''}
                {' '}
                found
              </div>
            </div>
          </div>

          {/* Success/Error Messages */}
          {successMsg && (
            <div className="mx-6 mt-4 bg-green-900/20 border border-green-700 text-green-300 px-4 py-3 rounded-lg">
              <p className="text-center">{successMsg}</p>
            </div>
          )}
          {errorMsg && (
            <div className="mx-6 mt-4 bg-red-900/20 border border-red-700 text-red-300 px-4 py-3 rounded-lg">
              <p className="text-center">{errorMsg}</p>
            </div>
          )}

          {/* Table Section */}
          {filteredLessons && filteredLessons.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-750">
                  <tr className="border-b border-gray-600">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {paginatedLessons.map(({
                    id, title, description, source, sourceDetail, dateReported,
                  }, index) => (
                    <tr key={id} className={`hover:bg-gray-750 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}`}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-white">
                          {title}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-300 max-w-xs truncate">
                          {description}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-300">{source}</span>
                          {sourceDetail && (
                            <span className="text-xs text-gray-400">{sourceDetail}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon icon={faCalendarAlt} className="text-sm text-gray-400" />
                          <span className="text-sm text-gray-300">
                            {new Date(dateReported).toLocaleDateString()}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal({
                              id, title, description, source, sourceDetail, dateReported,
                            })}
                            className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-all duration-200"
                            title="Edit Lesson"
                          >
                            <FontAwesomeIcon icon={faEdit} className="text-sm" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteLesson(id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-all duration-200"
                            title="Delete Lesson"
                          >
                            <FontAwesomeIcon icon={faTrash} className="text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faLightbulb} className="text-2xl text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No lessons found</h3>
              <p className="text-gray-400 mb-6">Get started by creating your first lesson</p>
              <button
                type="button"
                onClick={() => setOpenAddModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Create First Lesson
              </button>
            </div>
          )}

          {/* Pagination */}
          {filteredLessons && filteredLessons.length > 0 && (
            <div className="px-6 py-4 bg-gray-750 border-t border-gray-600">
              <Pagination
                itemsDisplaying={paginatedLessons.length}
                itemsPerPage={PageSize}
                items={filteredLessons}
                handleNext={() => setCurrentPage(currentPage + 1)}
                handlePrevious={() => setCurrentPage(currentPage - 1)}
              />
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="w-80 bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
          {/* Lesson Statistics */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-blue-400" />
              Lesson Statistics
            </h3>
            <div className="space-y-4">
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Total Lessons</span>
                  <span className="text-white font-semibold">{lessons?.length || 0}</span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">This Month</span>
                  <span className="text-blue-400 font-semibold">
                    {(() => {
                      const currentMonth = new Date().getMonth();
                      const currentYear = new Date().getFullYear();
                      return lessons?.filter((lesson) => {
                        const lessonDate = new Date(lesson.dateReported);
                        return lessonDate.getMonth()
                        === currentMonth && lessonDate.getFullYear() === currentYear;
                      }).length || 0;
                    })()}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">This Year</span>
                  <span className="text-green-400 font-semibold">
                    {(() => {
                      const currentYear = new Date().getFullYear();
                      return lessons?.filter((lesson) => {
                        const lessonDate = new Date(lesson.dateReported);
                        return lessonDate.getFullYear() === currentYear;
                      }).length || 0;
                    })()}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Total Views</span>
                  <span className="text-purple-400 font-semibold">2,847</span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Lessons */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Lessons</h3>
            <div className="space-y-3">
              {lessons?.slice(0, 3).map((lesson) => (
                <div key={lesson.id} className="bg-gray-750 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-white truncate">{lesson.title}</h4>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(lesson.dateReported).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                      <span className="text-xs text-gray-500">
                        {Math.floor(Math.random() * 30) + 5}
                        {' '}
                        views
                      </span>
                      <FontAwesomeIcon icon={faEye} className="text-xs text-gray-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setOpenAddModal(true)}
                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
              >
                <FontAwesomeIcon icon={faPlus} className="text-sm" />
                <span>Create Lesson</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <Modal show={openDeleteModal} size="md" onClose={() => setOpenDeleteModal(false)} popup>
        <Modal.Header className="bg-gray-700 border-b border-gray-600">
          <h3 className="text-lg font-semibold text-white">Delete Lesson</h3>
        </Modal.Header>
        <Modal.Body className="bg-gray-800">
          <div className="text-center p-6">
            <div className="w-16 h-16 bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faTrash} className="text-2xl text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Confirm Deletion</h3>
            <p className="text-gray-300 mb-6">Are you sure you want to delete this lesson? This action cannot be undone.</p>
            <div className="flex items-center justify-center space-x-4">
              <button
                type="button"
                onClick={() => setOpenDeleteModal(false)}
                className="px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteLesson}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Lesson
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Add/Edit Lesson Modal */}
      <Modal
        show={openAddModal || openEditModal}
        size="2xl"
        onClose={() => {
          setOpenAddModal(false);
          setOpenEditModal(false);
        }}
        popup
      >
        <Modal.Header className="bg-gray-700 border-b border-gray-600">
          <h3 className="text-lg font-semibold text-white">
            {openAddModal ? 'Create New Lesson' : 'Edit Lesson'}
          </h3>
        </Modal.Header>
        <Modal.Body className="bg-gray-800">
          <form onSubmit={openAddModal ? handleAddLesson : handleEditLesson} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-2">
                  Title *
                  <input
                    type="text"
                    id="title"
                    value={lessonForm.title}
                    onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400"
                    placeholder="Enter lesson title"
                    required
                  />
                </label>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
                  Description *
                  <textarea
                    id="description"
                    value={lessonForm.description}
                    onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400"
                    placeholder="Enter lesson description"
                    required
                  />
                </label>
              </div>
              <div>
                <label htmlFor="source" className="block text-sm font-medium text-gray-300 mb-2">
                  Source *
                  <input
                    type="text"
                    id="source"
                    value={lessonForm.source}
                    onChange={(e) => setLessonForm({ ...lessonForm, source: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400"
                    placeholder="Enter source"
                    required
                  />
                </label>
              </div>
              <div>
                <label htmlFor="dateReported" className="block text-sm font-medium text-gray-300 mb-2">
                  Date Reported *
                  <input
                    type="date"
                    id="dateReported"
                    value={lessonForm.dateReported}
                    onChange={(e) => setLessonForm({ ...lessonForm, dateReported: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400"
                    required
                  />
                </label>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="sourceDetail" className="block text-sm font-medium text-gray-300 mb-2">
                  Source Details
                  <input
                    type="text"
                    id="sourceDetail"
                    value={lessonForm.sourceDetail}
                    onChange={(e) => setLessonForm({ ...lessonForm, sourceDetail: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400"
                    placeholder="Enter additional source details"
                  />
                </label>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-4 mt-6">
              <button
                type="button"
                onClick={() => {
                  setOpenAddModal(false);
                  setOpenEditModal(false);
                }}
                className="px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {getButtonText()}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Lessons;
